/**
 * Admin Reports Page
 *
 * What people reported from the app: error toasts they pressed Report on, the
 * crash page's reports, bugs and feedback. Newest first, filtered by kind,
 * status, stage, user and date, searched by message. A row opens a drawer with
 * the full context, a status control (new, seen, resolved), an admin note, and
 * a link to the user.
 * Access: platform_admin.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bug,
  ChatCircle,
  Clock,
  Flag,
  ArrowsClockwise as RefreshCw,
  FloppyDisk as Save,
  User,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  SegmentedControl,
  StatusPill,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminReports, useUpdateAdminReport } from "@/hooks/useAdmin";
import type {
  AdminReport,
  AdminReportFilters,
  ReportKind,
  ReportStatus,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const KINDS: Array<{ value: "" | ReportKind; label: string }> = [
  { value: "", label: "All kinds" },
  { value: "error", label: "Errors" },
  { value: "bug", label: "Bugs" },
  { value: "feedback", label: "Feedback" },
  { value: "content", label: "Content" },
];

const STATUSES: Array<{ value: ReportStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "seen", label: "Seen" },
  { value: "resolved", label: "Resolved" },
];

const KIND_ICONS: Record<ReportKind, React.ElementType> = {
  error: WarningCircle,
  bug: Bug,
  feedback: ChatCircle,
  content: Flag,
};

const KIND_TONES: Record<ReportKind, PillTone> = {
  error: "orange",
  bug: "red",
  feedback: "blue",
  content: "amber",
};

const STATUS_TONES: Record<ReportStatus, PillTone> = {
  new: "blue",
  seen: "amber",
  resolved: "green",
};

const LIST_COLUMNS = "minmax(0,1fr) 100px 100px 110px";

const FIELD_CLASS =
  "h-9 rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function label(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Meta({
  name,
  children,
  wide,
}: {
  name: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn(wide && "col-span-2")}>
      <p className="text-xs text-muted-foreground">{name}</p>
      <div className="break-words text-xs">{children}</div>
    </div>
  );
}

// ── Detail drawer ──────────────────────────────────────────

function ReportDrawer({
  report,
  onClose,
  onChanged,
}: {
  report: AdminReport;
  onClose: () => void;
  onChanged: (updated: AdminReport) => void;
}) {
  const update = useUpdateAdminReport();
  const [note, setNote] = useState(report.adminNote ?? "");
  const noteChanged = note !== (report.adminNote ?? "");
  const KindIcon = KIND_ICONS[report.kind];
  const ctx = report.context;

  function save(data: { status?: ReportStatus; adminNote?: string }) {
    update.mutate(
      { reportId: report.reportId, data },
      { onSuccess: onChanged }
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Report detail"
        className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-border/50 bg-card shadow-xl sm:max-w-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <KindIcon className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Report</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="flex flex-wrap gap-2">
            <StatusPill tone={KIND_TONES[report.kind]}>
              {label(report.kind)}
            </StatusPill>
            <StatusPill tone={STATUS_TONES[report.status]}>
              {label(report.status)}
            </StatusPill>
            <StatusPill tone="neutral">{report.stage}</StatusPill>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
              Message
            </p>
            <p className="whitespace-pre-wrap text-sm">{report.message}</p>
          </div>

          {report.note && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                What the person added
              </p>
              <p className="whitespace-pre-wrap text-sm">{report.note}</p>
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              Context
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Meta name="Reported">{formatDate(report.createdAt)}</Meta>
              <Meta name="Happened">{formatDate(ctx.occurredAt)}</Meta>
              {ctx.route && (
                <Meta name="Route" wide>
                  {ctx.route}
                </Meta>
              )}
              {ctx.draftId && <Meta name="Draft">{ctx.draftId}</Meta>}
              {ctx.reportedHandle && (
                <Meta name="Reported person">@{ctx.reportedHandle}</Meta>
              )}
              {ctx.postId && <Meta name="Post">{ctx.postId}</Meta>}
              {ctx.requestId && (
                <Meta name="Request id" wide>
                  <span className="font-mono">{ctx.requestId}</span>
                </Meta>
              )}
              {report.personaId && (
                <Meta name="Persona">{report.personaId}</Meta>
              )}
              {ctx.appVersion && (
                <Meta name="App version">{ctx.appVersion}</Meta>
              )}
              {ctx.plan && <Meta name="Plan">{ctx.plan}</Meta>}
              {ctx.userAgent && (
                <Meta name="Browser" wide>
                  {ctx.userAgent}
                </Meta>
              )}
            </div>
            {ctx.stack && (
              <pre className="mt-3 max-h-40 overflow-auto rounded-lg bg-muted p-3 text-xs">
                {ctx.stack}
              </pre>
            )}
            {ctx.consoleErrors && (
              <div className="mt-3">
                <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  Console errors
                </p>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-muted p-3 text-xs">
                  {ctx.consoleErrors}
                </pre>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border/50 p-4">
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              User
            </p>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {report.userEmail ??
                    (report.userId === "anonymous"
                      ? "Not signed in"
                      : report.userId)}
                </p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {report.userId}
                </p>
              </div>
              {report.userId !== "anonymous" && (
                <Link
                  to={`/users/${report.userId}`}
                  className="inline-flex h-8 shrink-0 items-center rounded-full border border-input px-3 text-xs font-medium hover:bg-accent"
                >
                  Open user
                </Link>
              )}
            </div>
          </div>

          <div className="space-y-3 rounded-xl border border-border/50 bg-card p-4">
            <p className="text-sm font-semibold">Status</p>
            <SegmentedControl
              label="Report status"
              value={report.status}
              onChange={(value) => save({ status: value as ReportStatus })}
              options={STATUSES}
            />
            <div>
              <label
                htmlFor="report-admin-note"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Admin note
              </label>
              <textarea
                id="report-admin-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={5000}
                placeholder="Internal note about this report..."
                className="w-full resize-none rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
              />
            </div>
            <Button
              size="sm"
              pill
              onClick={() => save({ adminNote: note })}
              disabled={!noteChanged || update.isPending}
            >
              {update.isPending ? (
                <Spinner size="sm" className="mr-1.5" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              Save note
            </Button>
            {update.isError && (
              <p role="alert" className="text-xs text-destructive">
                {getErrorMessage(
                  update.error,
                  "Could not save the change. Try again."
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────

interface Filters {
  kind: "" | ReportKind;
  status: "" | ReportStatus;
  stage: string;
  userId: string;
  search: string;
  from: string;
  to: string;
}

const NO_FILTERS: Filters = {
  kind: "",
  status: "",
  stage: "",
  userId: "",
  search: "",
  from: "",
  to: "",
};

export function AdminReportsPage() {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  // The text boxes apply after a pause, like the other admin search boxes.
  const [typed, setTyped] = useState({ stage: "", userId: "", search: "" });
  const [cursor, setCursor] = useState<string | undefined>();
  const [previous, setPrevious] = useState<AdminReport[]>([]);
  const [changed, setChanged] = useState<Record<string, AdminReport>>({});
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) =>
        f.stage === typed.stage.trim() &&
        f.userId === typed.userId.trim() &&
        f.search === typed.search.trim()
          ? f
          : {
              ...f,
              stage: typed.stage.trim(),
              userId: typed.userId.trim(),
              search: typed.search.trim(),
            }
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [typed]);

  // A new set of filters starts the list over.
  useEffect(() => {
    setCursor(undefined);
    setPrevious([]);
  }, [filters]);

  const query: AdminReportFilters = {
    kind: filters.kind || undefined,
    status: filters.status || undefined,
    stage: filters.stage || undefined,
    userId: filters.userId || undefined,
    search: filters.search || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    cursor,
    limit: 25,
  };
  const { data, isLoading, error, refetch, isFetching } =
    useAdminReports(query);

  const rows = [...previous, ...(data?.items ?? [])].map(
    (r) => changed[r.reportId] ?? r
  );
  const open = openId ? rows.find((r) => r.reportId === openId) : undefined;
  const filtered =
    Object.values(filters).some(Boolean) ||
    Object.values(typed).some((v) => v.trim());

  function setFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function loadMore() {
    if (data?.items) setPrevious((p) => [...p, ...data.items]);
    setCursor(data?.cursor);
  }

  function clear() {
    setFilters(NO_FILTERS);
    setTyped({ stage: "", userId: "", search: "" });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Reports"
        description="What people reported from the app: errors, bugs, feedback and other people's posts in Discover."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
        tabs={{
          label: "Report kind",
          items: KINDS,
          value: filters.kind,
          onChange: (v) => setFilter("kind", v as Filters["kind"]),
        }}
      />

      <div
        className="flex flex-wrap items-end gap-3"
        role="search"
        aria-label="Filter reports"
      >
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Search
          <input
            type="search"
            className={cn(FIELD_CLASS, "w-56")}
            placeholder="Search the message"
            value={typed.search}
            onChange={(e) =>
              setTyped((t) => ({ ...t, search: e.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Status
          <select
            className={FIELD_CLASS}
            value={filters.status}
            onChange={(e) =>
              setFilter("status", e.target.value as Filters["status"])
            }
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Stage
          <input
            className={cn(FIELD_CLASS, "w-32")}
            placeholder="e.g. staging"
            value={typed.stage}
            onChange={(e) => setTyped((t) => ({ ...t, stage: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          User id
          <input
            className={cn(FIELD_CLASS, "w-44")}
            placeholder="User id"
            value={typed.userId}
            onChange={(e) =>
              setTyped((t) => ({ ...t, userId: e.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          From
          <input
            type="date"
            className={FIELD_CLASS}
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => setFilter("from", e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          To
          <input
            type="date"
            className={FIELD_CLASS}
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => setFilter("to", e.target.value)}
          />
        </label>
        {filtered && (
          <Button variant="ghost" size="sm" onClick={clear}>
            Clear filters
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(error, "Could not load reports. Refresh to retry.")}
        </Alert>
      ) : (
        <>
          <DataTable>
            <DataTableHead columns={LIST_COLUMNS}>
              <span>Report</span>
              <span>Kind</span>
              <span>Status</span>
              <span>Stage</span>
            </DataTableHead>
            <DataTableBody>
              {rows.map((report) => {
                const KindIcon = KIND_ICONS[report.kind];
                return (
                  <DataTableRow
                    key={report.reportId}
                    columns={LIST_COLUMNS}
                    onClick={() => setOpenId(report.reportId)}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Open report: ${report.message}`}
                      className="flex min-w-0 items-start gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenId(report.reportId);
                        }
                      }}
                    >
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <KindIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p
                          className={cn(
                            "truncate",
                            report.status === "new" && "font-semibold"
                          )}
                        >
                          {report.message}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {report.userEmail ?? report.userId.slice(0, 8)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(report.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <StatusPill tone={KIND_TONES[report.kind]}>
                        {label(report.kind)}
                      </StatusPill>
                    </div>
                    <div>
                      <StatusPill tone={STATUS_TONES[report.status]}>
                        {label(report.status)}
                      </StatusPill>
                    </div>
                    <div className="truncate text-sm text-muted-foreground">
                      {report.stage}
                    </div>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
            {rows.length === 0 && (
              <EmptyState
                icon={ChatCircle}
                title="No reports found."
                description={
                  filtered
                    ? "Try different filters, or clear them."
                    : "Nothing has been reported yet."
                }
              />
            )}
          </DataTable>

          {data?.hasMore && data.cursor && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" pill onClick={loadMore}>
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      {open && (
        <ReportDrawer
          key={open.reportId}
          report={open}
          onClose={() => setOpenId(null)}
          onChanged={(updated) =>
            setChanged((c) => ({ ...c, [updated.reportId]: updated }))
          }
        />
      )}
    </div>
  );
}
